/* eslint-disable react/no-this-in-sfc */
import type { FC } from 'react';
import React, { Component, Fragment } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import EditorMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { layers } from '@atlaskit/theme/constants';

import { IN_EDITOR_TEMPLATES_LOAD_PANEL_CATEGORIES_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';

import { CategoryItem } from './CategoryItem.tsx';
import { LoadingCategories } from './LoadingCategories.tsx';
import {
	InlineDialogContentWrapper,
	CategoryList,
	IconWrapper,
	EllipsisPill,
} from './CategorySelectorStyles.tsx';
import { withErrorBoundary } from './WithErrorBoundary.tsx';
import type { Category } from './types.ts';

export const DEFAULT_NUM_RENDERED_CATEGORIES = 5;

const i18n = defineMessages({
	moreCategoriesButton: {
		id: 'category-selector.more.categories.button',
		defaultMessage: 'More categories',
		description:
			'The text is shown on a button in the category selector component that allows users to expand and view additional template categories beyond those initially displayed.',
	},
});

type CategorySelectorProps = {
	categories: Category[];
	selectedCategoryIds?: (string | null | undefined)[];
	numVisibleCategories?: number;
	isDisabled?: boolean;
	isLoading?: boolean;
	onCategorySelected?: (category: Category) => void;
};

type CategorySelectorState = {
	isMenuOpen: boolean;
};

class CategorySelectorInner extends Component<
	CategorySelectorProps & WrappedComponentProps,
	CategorySelectorState
> {
	static defaultProps = {
		numVisibleCategories: DEFAULT_NUM_RENDERED_CATEGORIES,
		isDisabled: false,
		onCategorySelected: () => {},
	};

	state = {
		isMenuOpen: false,
	};

	handleCategorySelected = (category: Category) => {
		const { onCategorySelected } = this.props;

		onCategorySelected!(category);
		if (this.state.isMenuOpen) {
			this.setState({ isMenuOpen: false });
		}
	};

	handleMoreClick = (e) => {
		e.stopPropagation();
		if (this.props.isDisabled) {
			return;
		}
		this.setState((prevState) => ({
			isMenuOpen: !prevState.isMenuOpen,
		}));
	};

	handleClose = () => {
		this.setState({ isMenuOpen: false });
	};

	getMenuContent = (categoriesInMenu: Category[]) => () => (
		<InlineDialogContentWrapper>
			<CategoryList data-testid="more-categories-list">
				{categoriesInMenu.map((category) => (
					<CategoryItem
						key={category.id}
						category={category}
						onClick={this.handleCategorySelected}
					/>
				))}
			</CategoryList>
		</InlineDialogContentWrapper>
	);

	getTriggerContent = (categoriesInMenu, visibleCategories) => (triggerProps) => {
		const { isDisabled, selectedCategoryIds, intl } = this.props;

		const handleIsSelected = (category) => selectedCategoryIds?.includes(category.id);

		return (
			<span {...triggerProps}>
				<CategoryList data-testid="categories-list" onClick={this.handleClose}>
					{visibleCategories.map((category) => (
						<CategoryItem
							key={category.id}
							category={category}
							isSelected={handleIsSelected(category)}
							isDisabled={isDisabled}
							onClick={this.handleCategorySelected}
						/>
					))}
					{!!categoriesInMenu.length && (
						<EllipsisPill
							data-testid="more-categories-ellipsis"
							onClick={this.handleMoreClick}
							isDisabled={isDisabled}
						>
							<Tooltip
								content={<FormattedMessage {...i18n.moreCategoriesButton} />}
								delay={500}
								hideTooltipOnClick
							>
								<IconWrapper>
									<EditorMoreIcon
										spacing="spacious"
										label={intl.formatMessage(i18n.moreCategoriesButton)}
									/>
								</IconWrapper>
							</Tooltip>
						</EllipsisPill>
					)}
				</CategoryList>
			</span>
		);
	};

	render() {
		const { categories, selectedCategoryIds, numVisibleCategories, isLoading } = this.props;

		let visibleCategories = categories;
		let categoriesInMenu: Category[] = [];

		visibleCategories = categories.reduce((acc: Category[], category, index) => {
			// If category's index is within the number of visible categories or it's selected, add it to the array
			if (index < numVisibleCategories! || selectedCategoryIds?.includes(category.id)) {
				acc.push(category);
			}

			return acc;
		}, []);

		// Create a new array where only the unique values of these two arrays appear in expand popup menu
		categoriesInMenu = [
			...visibleCategories.filter((element) => !categories.includes(element)),
			...categories.filter((element) => !visibleCategories.includes(element)),
		];

		if (isLoading) {
			return <LoadingCategories />;
		}

		return (
			<Fragment>
				<ExperienceSuccess name={IN_EDITOR_TEMPLATES_LOAD_PANEL_CATEGORIES_EXPERIENCE} />
				<Popup
					isOpen={this.state.isMenuOpen}
					zIndex={layers.navigation()}
					placement="bottom"
					onClose={this.handleClose}
					content={this.getMenuContent(categoriesInMenu)}
					trigger={this.getTriggerContent(categoriesInMenu, visibleCategories)}
					shouldRenderToParent
				/>
			</Fragment>
		);
	}
}

export const CategorySelector: FC<CategorySelectorProps> = withErrorBoundary(
	injectIntl(CategorySelectorInner),
);
